import React, { useState, useEffect } from 'react';
import Modal from '../../components/Modal/Modal';
import AddEntity from '../../components/AddEntity/AddEntity';
import ApiManager from '../../ApiManager/ApiManager';
import { useComponentLoading } from '../../utils/useComponentLoading';
import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';

const NewFuelTransaction = ({onSuccess}) => {
    const [isModalOpen, setModalOpen] = useState(false);
    const [carOptions, setCarOptions] = useState([]);
    const [driverOptions, setDriverOptions] = useState([]);
    const [fuelFields, setFuelFields] = useState([]);
    const [carMap, setCarMap] = useState({});
    const [driverMap, setDriverMap] = useState({});
    const [fileResponse, setFileResponse] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [showLoading, setShowLoading] = useState(false);
    
    // Use the loading hook
    const { withLoading, isLoading, getMessage, getProgress } = useComponentLoading();

    const fetchDriverData = async () => {
        try {
            const params = new URLSearchParams({
                page_size: 1000,
                page: 1,
              });
            const response = await ApiManager.getAllDrivers(params);
            if (!response.ok) throw new Error('Network response was not ok');
            const json = await response.json();
            const driverNames = json.map((driver) => driver.firstName);
            const driverIdMap = json.reduce((map, driver) => {
                map[driver.firstName] = driver.id;
                return map;
            }, {});
            setDriverOptions(driverNames);
            setDriverMap(driverIdMap);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const fetchCarData = async () => {
        try {
            const params = new URLSearchParams({
                page_size: 1000,
                page: 1,
              });
            const response = await ApiManager.getAllCars(params);
            if (!response.ok) throw new Error('Network response was not ok');
            const json = await response.json();
            const carNumbers = json.map((car) => car.vehicleNo);
            const carIdMap = json.reduce((map, car) => {
                map[car.vehicleNo] = car.id;
                return map;
            }, {});
            setCarOptions(carNumbers);
            setCarMap(carIdMap);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };
    
    // Add a function to load all required data
    const loadData = async () => {
        if (dataLoaded) return;
        
        setShowLoading(true);
        
        await withLoading('loadNewFuelData', async (onProgress) => {
            onProgress(10);
            await fetchDriverData();
            onProgress(50);
            await fetchCarData();
            onProgress(100);
            
            // Add a small delay to ensure the loading indicator is visible
            setTimeout(() => {
                setDataLoaded(true);
                setShowLoading(false);
            }, 500);
        }, 'Loading driver and car data...', 30000);
    };

    useEffect(() => {
        setFuelFields([
            ...(carOptions.length > 0
                ? [{ label: 'Car', name: 'car_id', type: 'picklist', required: true, options: carOptions }]
                : [{ label: 'Car', name: 'car_id', type: 'picklist', required: true, options:[]}]),
            ...(driverOptions.length > 0
                ? [{ label: 'Driver', name: 'driver_id', type: 'picklist', required: true, options: driverOptions }]
                : [{ label: 'Driver', name: 'driver_id', type: 'picklist', required: true, options:[]}]),
            { label: 'Fuel Quantity (L)', name: 'fuel_amount', type: 'number', required: true },
            { label: 'Amount', name: 'amount', type: 'number', required: true },
            { label: 'Km Reading', name: 'km_reading', type: 'number', required: true },
            { label: 'Fuel Type', name: 'fuel_type', type: 'picklist', required: true, options:['PETROL', 'DIESEL', 'CNG', 'ELECTRIC', 'OTHER']},
            { label: 'Fuel Receipt', name: 'fuel_receipt', type: 'file', required: false },
            { label: 'km reading file', name: 'km_reading_file', type: 'file', required: false },
            { label: 'Serial Number', name: 'description', type: 'text', required: false },
            { label: 'Driver Paid', name: 'is_paid_by_driver', type: 'checkbox', required: false},
        ]);
      
    }, [carOptions,driverOptions]);

    const toggleModal = async () => {
        // If opening the modal, load the data first
        setModalOpen(!isModalOpen);
        if (!isModalOpen) {
            await loadData();
        }
        
    };

    const handleAddFuelTransaction = async (formData) => {
        console.log("fileResponse", fileResponse);

        const carId = carMap[formData.car_id];
        const driverId = driverMap[formData.driver_id];

        const updatedFormData = { 
            ...formData, 
            car_id: carId, 
            driver_id: driverId 
        };

        if (fileResponse?.fuel_receipt) {
            updatedFormData.file_path_fuel = fileResponse.fuel_receipt;
        }
        if (fileResponse?.km_reading_file) {
            updatedFormData.file_path_km_reading = fileResponse.km_reading_file;
        }
        const response = await ApiManager.addFuelTransaction(updatedFormData);
        return response;
    };

    // Get loading state for the modal
    const isDataLoading = isLoading('loadNewFuelData');
    const loadingMessage = getMessage('loadNewFuelData');
    const loadingProgress = getProgress('loadNewFuelData');
    
    console.log("Loading state:", isDataLoading);
    console.log("Loading message:", loadingMessage);
    console.log("Loading progress:", loadingProgress);
    console.log("Show loading:", showLoading);

    return (
        <div>
                <button className="custom-button" onClick={toggleModal}>
                    New Fuel Transaction
                </button>
            <Modal isOpen={isModalOpen} onClose={toggleModal} title="Add Fuel Transaction">
                {showLoading ? (
                    <div className="loading-container">
                        <LoadingSpinner 
                            size="large" 
                            message={loadingMessage || "Loading data..."} 
                            progress={loadingProgress}
                            showProgress={true}
                        />
                    </div>
                ) : (
                    <AddEntity
                        toggleFunction={toggleModal}
                        formFields={fuelFields}
                        apiFunction={handleAddFuelTransaction}
                        setFileResponse={setFileResponse}
                        fileResponse={fileResponse}
                        onSuccess={onSuccess}
                    />
                )}
            </Modal>
        </div>
    );
};

export default NewFuelTransaction;
